import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import Button from 'components/field/Button';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import ProgressBar from 'components/global/ProgressBar';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';

import { ACCOUNT_TYPES } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import { faGear, faPlus } from '@fortawesome/pro-light-svg-icons';

import CreateOtoLink from './CreateOTOLink/CreateOtoLink';
import OTOLinkOnboarding from './OTOLinkOnboarding/OTOLinkOnboarding';
import OtoLinkSettings from './OTOLinkSettings/OtoLinkSettings';
import { getOTOLinkSettings } from './OTOLinkSettings/OtoLinkSettingsSlice';

const STAGES = ['phoneVerification', 'address', 'accountType', 'bankAccount', 'paymentMethod', 'shippingCompany'];

const OTOLink = () => {
	const { settings: otoLinkSettings } = useSelector((state) => state.otoLinkSettings);
	const user = useSelector((state) => state.user);
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const tabs = useMemo(
		() => [
			{
				label: 'allLinks',
				path: mergePaths(PATHS.OTO_LINK, PATHS.ALL_LINKS)
			},
			{
				label: 'activeLinks',
				path: mergePaths(PATHS.OTO_LINK, PATHS.ACTIVE_LINKS)
			},
			{
				label: 'orders',
				path: mergePaths(PATHS.OTO_LINK, PATHS.ORDERS)
			},
			{
				label: 'transactions',
				path: mergePaths(PATHS.OTO_LINK, PATHS.OTO_LINK_TRANSACTIONS)
			}
		],
		[]
	);

	useEffect(() => {
		dispatch(getOTOLinkSettings()).then(() => {
			setIsLoading(false);
		});
	}, []);

	const openModal = (modal) => {
		NiceModal.show(modal);
	};

	const progressPercentage = useMemo(() => {
		if (isLoading) {
			return {
				ratio: 0,
				completedStages: []
			};
		}

		const totalStages = STAGES.length;
		const completedStages = [];

		const generalSettings = user?.generalSettings || {};

		if (user.mobileVerified) {
			completedStages.push('phoneVerification');
		}
		if (generalSettings?.defaultWarehouse) {
			completedStages.push('address');
		}

		if (generalSettings?.invoiceType !== ACCOUNT_TYPES.PERSONAL) {
			if (generalSettings?.civilID) {
				if (
					(ACCOUNT_TYPES.COMPANY === generalSettings?.invoiceType &&
						generalSettings?.CRNumber &&
						generalSettings?.CRDocumentURL) ||
					(ACCOUNT_TYPES.FREELANCER === generalSettings?.invoiceType && generalSettings?.freelanceDocument)
				) {
					completedStages.push('accountType');
				}
			}
		} else {
			completedStages.push('accountType');
		}

		if (generalSettings?.BankIban && generalSettings?.BankName && generalSettings?.BankHolderName) {
			completedStages.push('bankAccount');
		}

		if (otoLinkSettings?.allowedPaymentMethods) {
			completedStages.push('paymentMethod');
		}

		if (otoLinkSettings?.allowedDCs?.length > 0) {
			completedStages.push('shippingCompany');
		}

		return {
			ratio: Math.round((completedStages.length / totalStages) * 100),
			completedStages
		};
	}, [user.mobileVerified, user?.generalSettings, otoLinkSettings, isLoading]);

	const canUserCreateOtoLink = useMemo(() => {
		return (
			otoLinkSettings.linkSettingsId &&
			['phoneVerification', 'address', 'accountType', 'paymentMethod', 'shippingCompany'].every((item) =>
				progressPercentage.completedStages.includes(item)
			)
		);
	}, [progressPercentage, otoLinkSettings]);

	useEffect(() => {
		if (!isLoading && progressPercentage.ratio < 100 && !canUserCreateOtoLink) {
			openModal(OTOLinkOnboarding);
		}
	}, [isLoading, progressPercentage, canUserCreateOtoLink]);

	return (
		<div>
			<PageHeader
				title="otoLink"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/oto-link-icon.json.gz"
				moduleName="oto-link">
				{!isLoading && progressPercentage.ratio < 100 && (
					<Button variant="transparent" onClick={() => openModal(OTOLinkOnboarding)}>
						<ProgressBar variant="circular" value={progressPercentage.ratio} showValue />
						<Typography variant="p3" fontWeight="500">
							setUpYourAccount
						</Typography>
					</Button>
				)}

				<Button label="settings" variant="outline" icon={faGear} onClick={() => openModal(OtoLinkSettings)} />
				{canUserCreateOtoLink && (
					<Button label="createOTOLink" icon={faPlus} onClick={() => openModal(CreateOtoLink)} />
				)}
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default OTOLink;
